import { useState, useEffect, useCallback } from 'react';
import { hrAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Plus, Trash2, CheckCircle2, Circle } from 'lucide-react';
import { toast } from 'sonner';
import { STATUS_STYLES, formatDateShort } from './hrUtils';

export const TasksAdminTab = () => {
  const [tasks, setTasks] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({ employee_id: '', title: '', description: '', due_date: '', kind: 'onboarding' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    hrAPI.listTasks({}).then((res) => setTasks(res.data)).catch(() => setTasks([]));
  }, []);

  useEffect(() => {
    load();
    hrAPI.listEmployees().then((res) => setEmployees(res.data)).catch(() => {});
  }, [load]);

  const create = async () => {
    if (!form.employee_id || !form.title.trim()) { toast.error('Employee and title are required'); return; }
    setSaving(true);
    try {
      await hrAPI.createTask({ ...form, due_date: form.due_date || null, description: form.description || null });
      toast.success('Task assigned');
      setForm({ employee_id: form.employee_id, title: '', description: '', due_date: '', kind: form.kind });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign task');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await hrAPI.deleteTask(id);
      toast.success('Task deleted');
      load();
    } catch (err) {
      toast.error('Failed to delete task');
    }
  };

  if (!tasks) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>;

  return (
    <div className="space-y-6" data-testid="hr-admin-tasks-tab">
      <Card className="bg-white border-slate-200">
        <CardHeader><CardTitle className="text-slate-800 text-lg flex items-center gap-2"><Plus className="w-5 h-5 text-cyan-500" /> Assign Onboarding / Training Task</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-slate-700">Employee</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger className="bg-white border-slate-200 mt-1" data-testid="hr-admin-task-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} ({e.role})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-700">Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Complete security training" className="bg-white border-slate-200 mt-1" data-testid="hr-admin-task-title-input" />
            </div>
            <div>
              <Label className="text-slate-700">Type</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger className="bg-white border-slate-200 mt-1" data-testid="hr-admin-task-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="onboarding">Onboarding</SelectItem>
                  <SelectItem value="training">Training</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-700">Due Date (optional)</Label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="bg-white border-slate-200 mt-1" data-testid="hr-admin-task-due-input" />
            </div>
          </div>
          <div>
            <Label className="text-slate-700">Description (optional)</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="bg-white border-slate-200 mt-1" data-testid="hr-admin-task-desc-input" />
          </div>
          <Button onClick={create} disabled={saving} className="bg-cyan-500 hover:bg-cyan-600 rounded-full" data-testid="hr-admin-task-create-btn">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Assign Task
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-white border-slate-200">
        <CardHeader><CardTitle className="text-slate-800 text-lg">All Assigned Tasks <Badge variant="secondary" className="ml-2">{tasks.length}</Badge></CardTitle></CardHeader>
        <CardContent className="space-y-2" data-testid="hr-admin-tasks-list">
          {tasks.length === 0 && <p className="text-sm text-slate-400 py-4">No tasks assigned yet.</p>}
          {tasks.map((t) => (
            <div key={t.id} className="flex items-start justify-between p-3 rounded-xl border border-slate-200 gap-3">
              <div className="flex items-start gap-3 min-w-0">
                {t.status === 'completed'
                  ? <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                  : <Circle className="w-5 h-5 text-slate-300 shrink-0 mt-0.5" />}
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-slate-800">{t.title}</p>
                    <Badge variant="secondary" className="capitalize">{t.kind}</Badge>
                    <Badge className={STATUS_STYLES[t.status] || ''}>{t.status}</Badge>
                  </div>
                  <p className="text-xs text-slate-500">
                    {t.employee_name}{t.due_date ? ` · due ${formatDateShort(t.due_date)}` : ''}
                    {t.completed_at ? ` · completed ${formatDateShort(t.completed_at)}` : ''}
                  </p>
                </div>
              </div>
              <Button size="icon" variant="ghost" className="rounded-full text-red-500 hover:text-red-600 shrink-0" onClick={() => remove(t.id)} data-testid={`hr-admin-task-delete-${t.id}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};
